import FleetModal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import { NAPSpecialConditionPayload } from '@/libs/fleet-api/services/nap';
import { FileData } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import { useMutation } from '@tanstack/react-query';
import {
	Checkbox,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Upload,
	UploadFile,
} from 'antd';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import { FC } from 'react';
import { MasterData } from '@/libs/fleet-api/services/master-data';

export type SpecialConditionModalConfig = {
	approverOptions: MasterData[];
	statusOptions: MasterData[];
	subjectOptions: MasterData[];
	prospectId: string;
} & (
	| {
			type: 'new';
	  }
	| {
			type: 'edit';
			record: NAPSpecialConditionPayload['specialConditionSubmissions'][number];
	  }
);

export const useSpecialConditionModal = () => {
	return useAsyncModal<
		SpecialConditionModalConfig,
		NAPSpecialConditionPayload['specialConditionSubmissions'][number]
	>();
};

const SpecialConditionModal: FC<ReturnType<typeof useSpecialConditionModal>> = (
	props,
) => {
	const { data } = props ?? {};
	const { form, fieldName } =
		useForm<
			NAPSpecialConditionPayload['specialConditionSubmissions'][number]
		>();
	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<FleetModal
			{...props.modal}
			width={940}
			closable={false}
			buttonPosition="end"
			okText="SAVE"
			cancelText="BACK"
			onCancel={() => props.close()}
			onOk={form.submit}
			okButtonProps={{
				disabled: uploadMutation.isPending,
			}}
			cancelButtonProps={{
				disabled: uploadMutation.isPending,
			}}
			afterOpenChange={(isOpen) => {
				if (!isOpen) {
					form.resetFields();
				} else if (props.data?.type === 'edit') {
					form.setFieldsValue(props.data.record);
				}
			}}
		>
			<FleetTitle>Informasi Pengajuan Kondisi Khusus</FleetTitle>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={(values) => {
					props.close(values);
				}}
			>
				<Form.Item
					name={fieldName('id')}
					hidden
				/>
				<Row
					gutter={40}
					style={{ marginBlock: 20 }}
				>
					<Col span={12}>
						<Form.Item
							label="Perihal"
							name={fieldName('subjectId')}
							rules={[rule.required()]}
							labelCol={{ span: 10, style: { alignSelf: 'start' } }}
						>
							<Select
								options={data?.subjectOptions.map((data) => {
									return {
										label: data.value,
										value: data.id,
									};
								})}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Ketentuan"
							name={fieldName('provision')}
							rules={[rule.required()]}
							labelCol={{ span: 10, style: { alignSelf: 'start' } }}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={24}>
						<Form.Item
							label="Usulan"
							name={fieldName('proposal')}
							rules={[rule.required()]}
							labelCol={{ span: 5, style: { alignSelf: 'start' } }}
						>
							<TextArea rows={2} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Form.Item
					label="Pemutus Terakhir"
					name={fieldName('finalApprover')}
					rules={[rule.required()]}
					labelCol={{ span: 24 }}
				>
					<Checkbox.Group>
						<Row gutter={[20, 20]}>
							{data?.approverOptions.map((data) => (
								<Col
									key={data.id}
									span={6}
								>
									<Checkbox value={data.id}>{data.value}</Checkbox>
								</Col>
							))}
						</Row>
					</Checkbox.Group>
				</Form.Item>
				<Divider />
				<Row gutter={40}>
					<Col span={24}>
						<Form.Item
							label="Pengajuan Kondisi Khusus"
							name={fieldName('specialCondition')}
							rules={[rule.required()]}
							labelCol={{ span: 5, style: { alignSelf: 'start' } }}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={24}>
						<Form.Item
							name={fieldName('specialConditionStatusId')}
							label="Status Pengajuan Kondisi Khusus"
							labelCol={{ span: 5, style: { alignSelf: 'start' } }}
							rules={[rule.required()]}
						>
							<Select
								options={data?.statusOptions.map((data) => {
									return {
										label: data.value,
										value: data.id,
									};
								})}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={24}>
						<Form.Item
							name={fieldName('evidenceFile')}
							label="Upload Foto"
							labelCol={{ span: 5, style: { alignSelf: 'start' } }}
							rules={[rule.required()]}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name,
													thumbUrl: file.url,
												},
											] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
						>
							<Upload.Dragger
								listType="picture"
								accept=".jpg,.jpeg,.png"
								maxCount={1}
								beforeUpload={() => false}
								onRemove={() => {
									form.setFieldValue(fieldName('evidenceFile'), null);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = `customer/${data?.prospectId}/special-condition`;

									try {
										const url = await uploadMutation.mutateAsync({
											file: file as unknown as File,
											fileid,
											filepath,
										});
										form.setFieldValue(fieldName('evidenceFile'), {
											format: 'jpeg',
											id: fileid,
											name: file.name,
											path: filepath,
											url,
										});
									} catch {
										form.setFieldValue(fieldName('evidenceFile'), null);
									}
								}}
							>
								{uploadMutation.isPending ? (
									<LoadingOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								) : (
									<UploadOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								)}
							</Upload.Dragger>
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</FleetModal>
	);
};

export default SpecialConditionModal;
